import { useQuery } from "@tanstack/react-query";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

const TOGGLES = [
  { key: "exclude_dnc", label: "Exclure refus démarchage (*)", danger: true },
  { key: "has_email", label: "E-mail valide" },
  { key: "has_website", label: "Site web" },
  { key: "has_leader", label: "Dirigeant connu" },
];

const ALL = "__all__";

const FilterSelect = ({ value, onChange, placeholder, options, testId }) => (
  <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
    <SelectTrigger data-testid={testId} className="w-full md:w-48"><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL}>{placeholder}</SelectItem>
      {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const LeadFilters = ({ filters, setFilters, jobId, clearJob, reset }) => {
  const { data: cities = [] } = useQuery({ queryKey: ["cities"], queryFn: () => api.get("/leads/cities").then((r) => r.data), refetchInterval: 15000 });
  const set = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  return (
    <div data-testid="lead-filters" className="mb-4 rounded-lg border border-border bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
        <div className="relative md:w-72">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="leads-search-input" value={filters.q} onChange={(e) => set("q", e.target.value)} placeholder="Raison sociale, e-mail, dirigeant…" className="pl-9" />
        </div>
        <FilterSelect testId="filter-city-select" value={filters.city} onChange={(v) => set("city", v)} placeholder="Toutes les villes" options={cities.map((c) => ({ value: c, label: c }))} />
        <FilterSelect
          testId="filter-source-select"
          value={filters.source}
          onChange={(v) => set("source", v)}
          placeholder="Toutes les sources"
          options={[{ value: "localch", label: "Local.ch" }, { value: "searchch", label: "Search.ch" }, { value: "google", label: "Google Maps" }, { value: "zefix", label: "Zefix" }]}
        />
        <FilterSelect
          testId="filter-legal-select"
          value={filters.legal_status}
          onChange={(v) => set("legal_status", v)}
          placeholder="Statut légal"
          options={[{ value: "actif", label: "Actif (RC)" }, { value: "radié", label: "Radié" }, { value: "en liquidation", label: "En liquidation" }, { value: "non trouvé", label: "Non trouvé au RC" }, { value: "inconnu", label: "Non vérifié" }]}
        />
        <Button variant="ghost" size="sm" data-testid="filters-reset-button" onClick={reset} className="md:ml-auto">Réinitialiser</Button>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-border pt-4">
        {TOGGLES.map((t) => (
          <label key={t.key} className="flex cursor-pointer items-center gap-2 text-sm">
            <Switch data-testid={`filter-${t.key}-switch`} checked={filters[t.key]} onCheckedChange={(v) => setFilters((f) => ({ ...f, [t.key]: v, ...(t.key === "exclude_dnc" && v ? { only_dnc: false } : {}) }))} />
            <span className={t.danger ? "font-semibold text-[#FF3B30]" : ""}>{t.label}</span>
          </label>
        ))}
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <Switch data-testid="filter-only_dnc-switch" checked={filters.only_dnc} onCheckedChange={(v) => setFilters((f) => ({ ...f, only_dnc: v, ...(v ? { exclude_dnc: false } : {}) }))} />
          Uniquement refus
        </label>
        {jobId && (
          <span data-testid="job-filter-chip" className="inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-1 text-xs font-medium text-[#002FA7]">
            Recherche #{jobId.slice(-6)}
            <button data-testid="job-filter-clear" onClick={clearJob}><X className="h-3 w-3" /></button>
          </span>
        )}
      </div>
    </div>
  );
};
